import { OrderTable } from '@services/product/order/order.types';
import { formatDate } from '@utils/dates';
import { formatMoney } from '@utils/numbers';

interface GroupedOrderProduct {
    productName: string;
    orders: OrderTable[];
    totalQuantity: number;
    totalAmount: number;
}

const PRINT_STYLES = `
    body { 
        font-family: Arial, sans-serif; 
        margin: 16px; 
        font-size: 14px; 
        color: #000; 
        line-height: 1.4;
    }
    
    h1 { 
        text-align: center; 
        border-bottom: 3px solid #000; 
        padding-bottom: 10px; 
        margin-bottom: 20px; 
        font-size: 22px;
        font-weight: bold;
    }
    
    .summary {
        margin-bottom: 30px;
    }
    
    .summary-table {
        width: 60%;
        margin: 0 auto 25px auto;
        border-collapse: collapse;
        font-size: 14px;
    }
    
    .summary-table th {
        background: #2c3e50;
        color: white;
        padding: 12px 16px;
        text-align: center;
        font-weight: bold;
        border: 1px solid #34495e;
        font-size: 15px;
    }
    
    .summary-table td {
        padding: 12px 16px;
        text-align: center;
        border: 1px solid #bdc3c7;
        background: #ecf0f1;
        font-weight: bold;
        color: #2c3e50;
    }
    
    .summary-table .label-cell {
        text-align: left;
        background: #f8f9fa;
        font-weight: bold;
        color: #495057;
        font-size: 14px;
    }
    
    .summary-table .value-cell {
        text-align: right;
        background: white;
        color: #2c3e50;
        font-weight: bold;
        font-size: 14px;
    }

    .product-group {
        margin-bottom: 35px;
        page-break-inside: avoid;
    }

    .group-title {
        font-size: 16px;
        font-weight: bold;
        color: #2c3e50;
        background: #ecf0f1;
        padding: 12px 16px;
        margin: 0 0 8px 0;
        border-left: 5px solid #3498db;
    }
    
    table { 
        width: 100%; 
        border-collapse: collapse; 
        margin: 8px 0; 
        font-size: 12px;
    }
    
    th, td { 
        border: 1px solid #ccc; 
        padding: 8px 10px; 
        text-align: left; 
        vertical-align: top;
        line-height: 1.3;
    }
    
    th { 
        background: #f0f0f0; 
        font-weight: bold; 
        font-size: 13px;
        padding: 10px 12px;
    }
    
    .text-center {
        text-align: center;
    }
    
    .text-right {
        text-align: right;
    }

    .subtotal-row {
        background: #f8f9fa;
        font-weight: bold;
        font-size: 13px;
    }

    .subtotal-label {
        background: #e9ecef;
        font-weight: bold;
        color: #495057;
        font-size: 13px;
        padding: 10px 12px;
    }

    .subtotal-value {
        background: #f8f9fa;
        font-weight: bold;
        color: #2c3e50;
        font-size: 13px;
        padding: 10px 12px;
    }

    .total-section {
        margin-top: 30px;
        border-top: 3px solid #2c3e50;
        padding-top: 15px;
    }

    .total-table {
        width: 100%;
        margin: 0;
    }

    .total-row {
        background: #2c3e50;
        color: white;
        font-weight: bold;
        font-size: 15px;
    }

    .total-label {
        text-align: left;
        padding: 15px 18px;
        font-size: 16px;
        font-weight: bold;
    }

    .total-value {
        padding: 15px 18px;
        font-size: 16px;
        font-weight: bold;
    }
    
    @page { 
        margin: 1.5cm; 
        size: A4 landscape;
    }
    
    @media print {
        .no-print {
            display: none;
        }
        .product-group {
            page-break-inside: avoid;
        }
    }
`;

export const generateOrdersPrintHTML = (orders: OrderTable[]): string => {
    const groupedOrders = orders.reduce((groups: { [key: string]: GroupedOrderProduct }, order) => {
        const groupKey = order.etickets?.[0]?.eticketType?.label ?? order?.orderProducts?.[0]?.name ?? 'Inconnu';
        const productName = `${groupKey}`;

        if (!groups[productName]) {
            groups[productName] = {
                productName,
                orders: [],
                totalQuantity: 0,
                totalAmount: 0
            };
        }

        groups[productName].orders.push(order);
        groups[productName].totalQuantity += 1;
        groups[productName].totalAmount += order.total;

        return groups;
    }, {});

    const sortedGroups = Object.values(groupedOrders).sort((a, b) => a.productName.localeCompare(b.productName));

    const totalCommandes = orders.length;
    const totalMontant = orders.reduce((sum, order) => sum + order.total, 0);

    const generateSummary = () => `
        <div class="summary">
            <table class="summary-table">
                <thead>
                    <tr>
                        <th colspan="2">RÉSUMÉ DE LA SÉLECTION</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="label-cell">Nombre de commandes</td>
                        <td class="value-cell">${totalCommandes}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Montant total</td>
                        <td class="value-cell">${formatMoney(totalMontant)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;

    const generateGroupedOrdersTable = () => {
        return sortedGroups
            .map(
                (group) => `
            <div class="product-group">
                <h2 class="group-title">${group.productName}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Date transaction</th>
                            <th>Numéro de transaction</th>
                            <th>Type produit</th>
                            <th class="text-right">PU TTC</th>
                            <th class="text-center">Quantité</th>
                            <th class="text-right">Montant TTC</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${group.orders
                            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                            .map(
                                (order) => `
                                <tr>
                                    <td>${formatDate(order.createdAt)}</td>
                                    <td class="text-center">#${order.woocommerceId}</td>
                                    <td>${group.productName}</td>
                                    <td class="text-right">${formatMoney(order.total)}</td>
                                    <td class="text-center">1</td>
                                    <td class="text-right">${formatMoney(order.total)}</td>
                                </tr>
                            `
                            )
                            .join('')}
                        <tr class="subtotal-row">
                            <td colspan="4" class="subtotal-label">Sous total</td>
                            <td class="text-center subtotal-value">${group.totalQuantity}</td>
                            <td class="text-right subtotal-value">${formatMoney(group.totalAmount)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        `
            )
            .join('');
    };

    const generateTotalRow = () => `
        <div class="total-section">
            <table class="total-table">
                <tr class="total-row">
                    <td class="total-label">Total</td>
                    <td class="text-center total-value">${totalCommandes} commande(s)</td>
                    <td class="text-right total-value">${formatMoney(totalMontant)} TTC</td>
                </tr>
            </table>
        </div>
    `;

    return `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <title>Commandes sélectionnées - ${orders.length} commande(s)</title>
            <style>${PRINT_STYLES}</style>
        </head>
        <body>
            <h1>LISTE DES COMMANDES SÉLECTIONNÉES</h1>
            ${generateSummary()}
            ${generateGroupedOrdersTable()}
            ${generateTotalRow()}
        </body>
        </html>
    `;
};
